/**
 * Filter bar for the Accounts list view.
 *
 * A quick account-name search with the high-traffic filters inline next to it
 * (agent/broker on client portals, sort, and the "Active Groups" toggle, which
 * drives the ACTIVE phase filter), plus an "Advanced search" collapsible card —
 * same pattern as MemberSearch. On the internal portal the advanced panel leads
 * with a tenant multi-select (cbs-portal parity), then Group Name / FEIN /
 * Group ID / Agent typeahead / Location / anticipated dates / Tags / Status.
 * Applied advanced filters surface as removable chips so the active query is
 * always visible. The status board (AccountsStatusBoard) owns the finer-grained
 * operational-status filter.
 */

import React from 'react';
import {Building2, ChevronDown, Search, SlidersHorizontal, X} from 'lucide-react';
import {Input} from '@common-web/components/ui/input';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Label} from '@common-web/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import type {AccountsSubStage, LifecyclePhase} from '@api/generated/client';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {useDebounce} from '@common-web/hooks/use-debounce';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {StateSelect, getStateName} from '@client/components/StateSelect';
import type {UsStateCode} from '@common/constants/usStates';
import type {GroupProcessType} from '@common/lib/groupProcessType';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';

export type AccountSortBy = 'lastActivityAt' | 'createdAt' | 'companyName';

/** Product tags on GroupMetadata — mirrors cbs-portal ACCOUNT_TAGS. */
export const ACCOUNT_PRODUCT_TAGS = ['VBA', 'HSA', 'Open Enrollment'] as const;
export type AccountProductTag = (typeof ACCOUNT_PRODUCT_TAGS)[number];

/**
 * Off-pipeline statuses offered next to the pipeline stages in the Status
 * filter. Stored as a soft-archive server-side; Lost is REJECTED.
 */
export type AccountArchivedStatus = 'ON_HOLD' | 'REJECTED';

const ARCHIVED_STATUS_OPTIONS: Array<{value: AccountArchivedStatus; label: string}> = [
  {value: 'ON_HOLD', label: 'On Hold'},
  {value: 'REJECTED', label: 'Lost'},
];

/** Group Type buttons — mutually exclusive; same wording as AnalyzerGroupBadge. */
const GROUP_TYPE_OPTIONS: Array<{value: GroupProcessType; label: string}> = [
  {value: 'ANALYZER', label: 'Analyzer'},
  {value: 'MONTHLY', label: 'Monthly'},
];

export interface AccountFilters {
  /** Account name (company name) quick search. */
  search: string;
  /** Federal EIN (matched against the account's RFC form data). */
  fein: string;
  /**
   * Lifecycle phase filter. Reserved for the coarse "Active Groups" toggle,
   * `?phase=` deep links, and internal-portal phase flooring. The advanced
   * "Status" buttons filter on `subStages` instead so pipeline stages that
   * share a phase (e.g. Synrgy's Prospect + SWOT Call, both LEAD) can be
   * filtered independently.
   */
  phase: LifecyclePhase[];
  /**
   * Canonical sub-stages selected via the advanced "Status" buttons — one
   * button per configured pipeline stage, contributing that stage's
   * `systemSubStage` set. Matches the kanban columns exactly.
   */
  subStages: AccountsSubStage[];
  /**
   * Off-pipeline statuses (On Hold / Lost) selected via the same advanced
   * "Status" buttons. Empty = pipeline accounts only.
   */
  archivedStatuses: AccountArchivedStatus[];
  /** Assigned agent / broker id, or null for any. Client-portal quick filter. */
  assignedBrokerId: string | null;
  /** Location — 2-letter state code (e.g. TX, WI), or null for any. */
  state: UsStateCode | null;
  /**
   * Tenant multi-select (internal advanced search). Empty = all tenants.
   * Replaces the former single `clientId` quick-row Select.
   */
  clientIds: string[];
  /**
   * Free-text agent / broker name or email (internal advanced — cbs-portal
   * "Search agents…").
   */
  agentSearch: string;
  /** GroupMetadata.productTags multi-select (internal advanced). */
  tags: AccountProductTag[];
  /**
   * Internal-portal advanced search (cbs-portal parity). Always empty on
   * client portals — the fields only render on the internal view.
   */
  groupName: string;
  groupId: string;
  /** yyyy-mm-dd bounds on the account's estimated start date. */
  estimatedStartFrom: string;
  estimatedStartTo: string;
  /**
   * Analyzer vs Monthly designation (`GroupMetadata.internalProcess`); null =
   * all. When set, accounts without a linked Group or a classified
   * internalProcess are excluded server-side — the same accounts the
   * AnalyzerGroupBadge hides for, since their designation is unknown.
   */
  groupProcessType: GroupProcessType | null;
  sortBy: AccountSortBy;
}

export const DEFAULT_ACCOUNT_FILTERS: AccountFilters = {
  search: '',
  fein: '',
  phase: [],
  subStages: [],
  archivedStatuses: [],
  assignedBrokerId: null,
  state: null,
  clientIds: [],
  agentSearch: '',
  tags: [],
  groupName: '',
  groupId: '',
  estimatedStartFrom: '',
  estimatedStartTo: '',
  groupProcessType: null,
  sortBy: 'lastActivityAt',
};

const BROKER_ANY = '__any__';

const SORT_OPTIONS: Array<{value: AccountSortBy; label: string}> = [
  {value: 'lastActivityAt', label: 'Recent activity'},
  {value: 'createdAt', label: 'Newest'},
  {value: 'companyName', label: 'Company A–Z'},
];

/** Demo-aligned display labels for known CBS tenants. */
function tenantDisplayLabel(name: string): string {
  const key = name.trim().toLowerCase().replace(/\s+/g, '');
  const known: Record<string, string> = {
    synrgy: 'SYNRGY',
    champ: 'Champion Health',
    championhealth: 'Champion Health',
    quantum: 'Quantum',
    healthcues: 'HealthCues',
  };
  return known[key] ?? name;
}

interface AccountFilterBarProps {
  filters: AccountFilters;
  onChange: (filters: AccountFilters) => void;
  /** Internal portal only: client options for the tenant multi-select. */
  clientOptions?: Array<{id: string; name: string}>;
  /**
   * Hide the inline broker `<Select>`. Broker users get the mine/team scope
   * controls (BrokerScopeControls) instead, which own the broker selection.
   * Also hidden on the internal portal (agent typeahead lives in advanced).
   */
  hideBrokerFilter?: boolean;
  /**
   * Internal portal only: `{shown} of {total}` result count rendered on the
   * right of the search row (cbs-portal's Search & Advanced Search card).
   */
  resultCount?: {shown: number; total: number};
}

export function AccountFilterBar({
  filters,
  onChange,
  clientOptions,
  hideBrokerFilter = false,
  resultCount,
}: AccountFilterBarProps): React.ReactElement {
  const isInternal = useIsInternal();
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  // Client portals keep the broker dropdown; internal uses agentSearch instead.
  const showBrokerSelect = !hideBrokerFilter && !isInternal;
  const brokers = trpc.account.listBrokers.useQuery({}, {enabled: showBrokerSelect});
  const config = useAccountsViewConfig();

  // Agent typeahead (internal): suggest matching agents while typing. The
  // free text itself still narrows the list (name/email substring), so
  // picking a suggestion just completes the text with the agent's exact name.
  const [agentSuggestOpen, setAgentSuggestOpen] = React.useState(false);
  const debouncedAgentSearch = useDebounce(filters.agentSearch, 250);
  const agentSuggestions = trpc.account.searchAgents.useQuery(
    {
      search: debouncedAgentSearch.trim(),
      clientIds: filters.clientIds.length > 0 ? filters.clientIds : undefined,
    },
    {enabled: isInternal && agentSuggestOpen && debouncedAgentSearch.trim().length >= 2},
  );

  // The ACTIVE phase has a dedicated inline "Active Groups" toggle next to the
  // search input; the advanced Status buttons cover the pipeline stages only.
  // Each Status button filters on its configured stage's canonical sub-stages
  // (not its phase), so stages that share a phase — e.g. Synrgy's Prospect and
  // SWOT Call, both LEAD — filter independently and match the kanban columns.
  const pipelineStages = config.stages.filter((s) => s.systemPhase !== 'ACTIVE');

  const stageSubStages = (stage: (typeof pipelineStages)[number]): AccountsSubStage[] =>
    Array.from(new Set(stage.subStages.map((s) => s.systemSubStage as AccountsSubStage)));

  const isStageSelected = (stage: (typeof pipelineStages)[number]): boolean => {
    const subs = stageSubStages(stage);
    return subs.length > 0 && subs.every((ss) => filters.subStages.includes(ss));
  };

  const selectedStages = pipelineStages.filter(isStageSelected);

  const toggleStage = (stage: (typeof pipelineStages)[number]): void => {
    const subs = stageSubStages(stage);
    const next = isStageSelected(stage)
      ? filters.subStages.filter((ss) => !subs.includes(ss))
      : Array.from(new Set([...filters.subStages, ...subs]));
    onChange({...filters, subStages: next});
  };

  const toggleArchivedStatus = (status: AccountArchivedStatus): void => {
    const next = filters.archivedStatuses.includes(status)
      ? filters.archivedStatuses.filter((s) => s !== status)
      : [...filters.archivedStatuses, status];
    onChange({...filters, archivedStatuses: next});
  };

  const togglePhase = (p: LifecyclePhase): void => {
    const next = filters.phase.includes(p)
      ? filters.phase.filter((x) => x !== p)
      : [...filters.phase, p];
    onChange({...filters, phase: next});
  };

  const toggleClientId = (id: string): void => {
    const next = filters.clientIds.includes(id)
      ? filters.clientIds.filter((x) => x !== id)
      : [...filters.clientIds, id];
    onChange({...filters, clientIds: next});
  };

  const toggleTag = (tag: AccountProductTag): void => {
    const next = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onChange({...filters, tags: next});
  };

  // Mutually exclusive: re-clicking the selected option clears back to "all".
  const toggleGroupType = (value: GroupProcessType): void => {
    onChange({...filters, groupProcessType: filters.groupProcessType === value ? null : value});
  };

  const showingActiveGroups = filters.phase.includes('ACTIVE');
  // The cbs-portal-parity fields (tenant, group name/id, agent, dates, tags)
  // only render on the internal portal, so they only count there.
  const internalAdvancedCount = isInternal
    ? filters.clientIds.length +
      (filters.groupName.trim() ? 1 : 0) +
      (filters.groupId.trim() ? 1 : 0) +
      (filters.agentSearch.trim() ? 1 : 0) +
      (filters.estimatedStartFrom ? 1 : 0) +
      (filters.estimatedStartTo ? 1 : 0) +
      filters.tags.length
    : 0;
  const advancedCount =
    (filters.fein.trim() ? 1 : 0) +
    selectedStages.length +
    filters.archivedStatuses.length +
    (filters.state ? 1 : 0) +
    (filters.groupProcessType ? 1 : 0) +
    internalAdvancedCount;
  const hasAnyFilter =
    advancedCount > 0 ||
    showingActiveGroups ||
    !!filters.assignedBrokerId ||
    filters.search.trim().length > 0;

  // Keep the advanced section visible while it holds applied filters (same
  // behavior as MemberSearch) so nothing filters the list invisibly.
  React.useEffect(() => {
    if (advancedCount > 0) setAdvancedOpen(true);
  }, [advancedCount]);

  const clientLabelById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clientOptions ?? []) {
      map.set(c.id, tenantDisplayLabel(c.name));
    }
    return map;
  }, [clientOptions]);

  const searchInput = (
    <>
      <Search
        aria-hidden
        className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none text-muted-foreground'
      />
      <Input
        placeholder='Search account name…'
        value={filters.search}
        onChange={(e) => onChange({...filters, search: e.target.value})}
        className='pl-9'
      />
    </>
  );

  // Quick-row filters shared by both layouts (the broker select only renders
  // on client portals).
  const quickControls = (
    <>
      {showBrokerSelect && (
        <Select
          value={filters.assignedBrokerId ?? BROKER_ANY}
          onValueChange={(v) =>
            onChange({...filters, assignedBrokerId: v === BROKER_ANY ? null : v})
          }
        >
          <SelectTrigger className='w-[180px]'>
            <SelectValue placeholder='All brokers' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={BROKER_ANY}>All brokers</SelectItem>
            {brokers.data?.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Select
        value={filters.sortBy}
        onValueChange={(v) => onChange({...filters, sortBy: v as AccountSortBy})}
      >
        <SelectTrigger className='w-44'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              Sort: {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type='button'
        size='sm'
        variant={showingActiveGroups ? 'default' : 'outline'}
        onClick={() => togglePhase('ACTIVE')}
      >
        <Building2 className='mr-1.5 h-4 w-4' />
        Active Groups
      </Button>

      {hasAnyFilter ? (
        <Button
          variant='ghost'
          size='sm'
          onClick={() => onChange({...DEFAULT_ACCOUNT_FILTERS, sortBy: filters.sortBy})}
        >
          Clear all
        </Button>
      ) : null}
    </>
  );

  // Advanced field grid + its Clear button, reused by the internal border-t
  // panel and the client Collapsible.
  const advancedPanelContent = (
    <>
      {/* Internal portal mirrors cbs-portal's AdvancedFiltersGrid field
                order: Tenant → Group Name → FEIN → Group ID → Agent → State →
                dates → Tags → Status. Client portals keep the original three
                fields. */}
      <div
        className={cn(
          'grid gap-4 md:grid-cols-2',
          isInternal ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
        )}
      >
        {isInternal && clientOptions ? (
          <div className='space-y-1.5 md:col-span-2 lg:col-span-4'>
            <Label>Tenant</Label>
            <div className='flex flex-wrap gap-1.5'>
              {clientOptions.map((c) => (
                <Button
                  key={c.id}
                  type='button'
                  size='sm'
                  variant={filters.clientIds.includes(c.id) ? 'default' : 'outline'}
                  onClick={() => toggleClientId(c.id)}
                >
                  {tenantDisplayLabel(c.name)}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {isInternal ? (
          <div className='space-y-1.5'>
            <Label htmlFor='filter-group-name'>Group Name</Label>
            <Input
              id='filter-group-name'
              placeholder='Legal name or DBA'
              value={filters.groupName}
              onChange={(e) => onChange({...filters, groupName: e.target.value})}
            />
          </div>
        ) : null}

        <div className='space-y-1.5'>
          <Label htmlFor='filter-fein'>FEIN</Label>
          <Input
            id='filter-fein'
            inputMode='numeric'
            placeholder={isInternal ? 'XX-XXXXXXX' : 'e.g. 123456789'}
            value={filters.fein}
            onChange={(e) => onChange({...filters, fein: e.target.value})}
          />
        </div>

        {isInternal ? (
          <div className='space-y-1.5'>
            <Label htmlFor='filter-group-id'>Group ID</Label>
            <Input
              id='filter-group-id'
              placeholder='Search by ID'
              value={filters.groupId}
              onChange={(e) => onChange({...filters, groupId: e.target.value})}
            />
          </div>
        ) : null}

        {isInternal ? (
          <div className='space-y-1.5'>
            <Label htmlFor='filter-agent'>Agent</Label>
            <div className='relative'>
              <Search
                aria-hidden
                className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none text-muted-foreground'
              />
              <Input
                id='filter-agent'
                placeholder='Search agents…'
                value={filters.agentSearch}
                autoComplete='off'
                onChange={(e) => {
                  onChange({...filters, agentSearch: e.target.value});
                  setAgentSuggestOpen(true);
                }}
                onFocus={() => {
                  if (filters.agentSearch.trim().length >= 2) setAgentSuggestOpen(true);
                }}
                onBlur={() => setAgentSuggestOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setAgentSuggestOpen(false);
                }}
                className='pl-9'
              />
              {agentSuggestOpen && (agentSuggestions.data?.length ?? 0) > 0 ? (
                <ul
                  className='absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-md border bg-popover py-1 shadow-md'
                  role='listbox'
                  aria-label='Agent suggestions'
                >
                  {agentSuggestions.data?.map((agent) => (
                    <li key={agent.id}>
                      <button
                        type='button'
                        role='option'
                        aria-selected={false}
                        // onMouseDown so the pick lands before the input's blur.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onChange({...filters, agentSearch: agent.name});
                          setAgentSuggestOpen(false);
                        }}
                        className='flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left text-sm hover:bg-muted'
                      >
                        <span className='flex w-full items-center justify-between gap-2'>
                          <span className='truncate font-medium'>{agent.name}</span>
                          <span className='shrink-0 text-xs text-muted-foreground'>
                            {tenantDisplayLabel(agent.client.name)}
                          </span>
                        </span>
                        {agent.contactEmail ? (
                          <span className='truncate text-xs text-muted-foreground'>
                            {agent.contactEmail}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className='space-y-1.5'>
          <Label>Location (state)</Label>
          <StateSelect
            value={filters.state ?? ''}
            onValueChange={(v) => onChange({...filters, state: (v as UsStateCode) || null})}
            placeholder='Any state'
            className='w-full'
          />
        </div>

        {isInternal ? (
          <>
            <div className='space-y-1.5'>
              <Label htmlFor='filter-start-from'>Anticipated Date From</Label>
              <Input
                id='filter-start-from'
                type='date'
                value={filters.estimatedStartFrom}
                onChange={(e) => onChange({...filters, estimatedStartFrom: e.target.value})}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='filter-start-to'>Anticipated Date To</Label>
              <Input
                id='filter-start-to'
                type='date'
                value={filters.estimatedStartTo}
                onChange={(e) => onChange({...filters, estimatedStartTo: e.target.value})}
              />
            </div>

            <div className='space-y-1.5 md:col-span-2 lg:col-span-4'>
              <Label>Tags</Label>
              <div className='flex flex-wrap gap-1.5'>
                {ACCOUNT_PRODUCT_TAGS.map((tag) => (
                  <Button
                    key={tag}
                    type='button'
                    size='sm'
                    variant={filters.tags.includes(tag) ? 'default' : 'outline'}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
            </div>
          </>
        ) : null}

        {/* Analyzer vs Monthly (GroupMetadata.internalProcess) — matches the tag shown
            on list rows, kanban cards, and the account/group detail headers. */}
        <div className='space-y-1.5'>
          <Label>Group Type</Label>
          <div className='flex flex-wrap gap-1.5'>
            {GROUP_TYPE_OPTIONS.map((option) => (
              <Button
                key={option.label}
                type='button'
                size='sm'
                variant={filters.groupProcessType === option.value ? 'default' : 'outline'}
                onClick={() => toggleGroupType(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div
          className={cn(
            'space-y-1.5',
            isInternal ? 'md:col-span-2 lg:col-span-4' : 'md:col-span-2 lg:col-span-1',
          )}
        >
          <Label>Status</Label>
          <div className='flex flex-wrap gap-1.5'>
            {pipelineStages.map((stage) => (
              <Button
                key={stage.key}
                type='button'
                size='sm'
                variant={isStageSelected(stage) ? 'default' : 'outline'}
                onClick={() => toggleStage(stage)}
              >
                {stage.label}
              </Button>
            ))}
            {/* Off-pipeline statuses: accounts marked On Hold / Lost leave the
                pipeline but stay reachable through these filters. */}
            {ARCHIVED_STATUS_OPTIONS.map((status) => (
              <Button
                key={status.value}
                type='button'
                size='sm'
                variant={filters.archivedStatuses.includes(status.value) ? 'default' : 'outline'}
                onClick={() => toggleArchivedStatus(status.value)}
              >
                {status.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className='flex justify-end'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          disabled={advancedCount === 0}
          onClick={() =>
            onChange({
              ...filters,
              fein: '',
              subStages: [],
              archivedStatuses: [],
              state: null,
              clientIds: [],
              agentSearch: '',
              tags: [],
              groupName: '',
              groupId: '',
              estimatedStartFrom: '',
              estimatedStartTo: '',
              groupProcessType: null,
            })
          }
        >
          Clear advanced
        </Button>
      </div>
    </>
  );

  const chipsRow =
    advancedCount > 0 ? (
      <div className='flex flex-wrap items-center gap-1.5'>
        {isInternal
          ? filters.clientIds.map((id) => (
              <FilterChip
                key={id}
                label={clientLabelById.get(id) ?? id}
                onRemove={() => toggleClientId(id)}
              />
            ))
          : null}
        {isInternal && filters.groupName.trim() ? (
          <FilterChip
            label={`Name: ${filters.groupName.trim()}`}
            onRemove={() => onChange({...filters, groupName: ''})}
          />
        ) : null}
        {filters.fein.trim() ? (
          <FilterChip
            label={`FEIN: ${filters.fein.trim()}`}
            onRemove={() => onChange({...filters, fein: ''})}
          />
        ) : null}
        {isInternal && filters.groupId.trim() ? (
          <FilterChip
            label={`Group ID: ${filters.groupId.trim()}`}
            onRemove={() => onChange({...filters, groupId: ''})}
          />
        ) : null}
        {isInternal && filters.agentSearch.trim() ? (
          <FilterChip
            label={`Agent: ${filters.agentSearch.trim()}`}
            onRemove={() => onChange({...filters, agentSearch: ''})}
          />
        ) : null}
        {selectedStages.map((stage) => (
          <FilterChip key={stage.key} label={stage.label} onRemove={() => toggleStage(stage)} />
        ))}
        {ARCHIVED_STATUS_OPTIONS.filter((s) => filters.archivedStatuses.includes(s.value)).map(
          (status) => (
            <FilterChip
              key={status.value}
              label={status.label}
              onRemove={() => toggleArchivedStatus(status.value)}
            />
          ),
        )}
        {filters.state ? (
          <FilterChip
            label={getStateName(filters.state)}
            onRemove={() => onChange({...filters, state: null})}
          />
        ) : null}
        {filters.groupProcessType ? (
          <FilterChip
            label={filters.groupProcessType === 'ANALYZER' ? 'Analyzer' : 'Monthly'}
            onRemove={() => onChange({...filters, groupProcessType: null})}
          />
        ) : null}
        {isInternal && filters.estimatedStartFrom ? (
          <FilterChip
            label={`From: ${filters.estimatedStartFrom}`}
            onRemove={() => onChange({...filters, estimatedStartFrom: ''})}
          />
        ) : null}
        {isInternal && filters.estimatedStartTo ? (
          <FilterChip
            label={`To: ${filters.estimatedStartTo}`}
            onRemove={() => onChange({...filters, estimatedStartTo: ''})}
          />
        ) : null}
        {isInternal
          ? filters.tags.map((tag) => (
              <FilterChip key={tag} label={tag} onRemove={() => toggleTag(tag)} />
            ))
          : null}
      </div>
    ) : null;

  // Internal portal: cbs-portal's "Search & Advanced Search" card — a single
  // white card with the search row (full-width input, quick filters, the
  // Advanced Search toggle button, result count) and the advanced grid in a
  // border-t panel below. Rendered inside the padded content area beneath the
  // status board, so no outer padding wrapper here. Client portals keep the
  // flush border-b strip.
  if (isInternal) {
    return (
      <div className='rounded-xl border bg-card'>
        <div className='flex flex-wrap items-center gap-3 px-5 py-4'>
          <div className='relative min-w-[280px] flex-1'>{searchInput}</div>
          {quickControls}
          <button
            type='button'
            onClick={() => setAdvancedOpen((o) => !o)}
            aria-expanded={advancedOpen}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-2 text-[13px] font-semibold transition-colors',
              // cbs-portal's Advanced Search toggle fills solid orange when
              // open or holding applied filters.
              advancedOpen || advancedCount > 0
                ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-card text-muted-foreground hover:bg-muted',
            )}
          >
            <SlidersHorizontal className='h-3.5 w-3.5' /> Advanced Search
            {advancedCount > 0 ? (
              <span className='flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/25 px-1 text-xs font-bold tabular-nums'>
                {advancedCount}
              </span>
            ) : null}
          </button>
          {resultCount ? (
            <span className='ml-auto text-xs tabular-nums text-muted-foreground'>
              {resultCount.shown} of {resultCount.total}
            </span>
          ) : null}
        </div>
        {chipsRow ? <div className='px-5 pb-4'>{chipsRow}</div> : null}
        {advancedOpen ? (
          <div className='space-y-4 border-t px-5 py-4'>{advancedPanelContent}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 border-b bg-background px-6 py-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <div className='relative w-full max-w-xs'>{searchInput}</div>
        {quickControls}
      </div>

      <Collapsible
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        className='rounded-xl border border-gray-200 bg-white shadow-sm'
      >
        <CollapsibleTrigger asChild>
          <button
            type='button'
            className='flex w-full items-center justify-between gap-3 rounded-t-xl px-4 py-3 text-left text-sm font-semibold text-brand-navy transition-colors hover:bg-gray-50/80'
          >
            <span className='flex items-center gap-2'>
              Advanced search
              {advancedCount > 0 ? <Badge variant='secondary'>{advancedCount}</Badge> : null}
            </span>
            <ChevronDown
              className={cn(
                'h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200',
                advancedOpen && 'rotate-180',
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className='space-y-4 border-t border-gray-100 px-4 pb-4 pt-3'>
            {advancedPanelContent}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {chipsRow}
    </div>
  );
}

function FilterChip({label, onRemove}: {label: string; onRemove: () => void}): React.ReactElement {
  return (
    <Badge variant='secondary' className='gap-1 pr-1'>
      {label}
      <button
        type='button'
        onClick={onRemove}
        className='rounded-sm p-0.5 hover:bg-muted-foreground/20'
        aria-label={`Remove ${label} filter`}
      >
        <X className='h-3 w-3' />
      </button>
    </Badge>
  );
}
